/**
 * Raster ToolCard → engine application.
 *
 * The card writes `useViewerStore.rasterToolCard`. This module is the single
 * place that turns that state into something a map engine can honour:
 *
 *   - Display (opacity / brightness / contrast / saturation) → MapLibre paint
 *     properties, OpenLayers opacity + canvas filter, Giro3D ColorLayer filters.
 *   - Source style (bands / color ramp / no-data mask) → TiTiler query rewrite
 *     on the live tile template (`setTiles` / source swap).
 *   - Blending → OpenLayers `globalCompositeOperation` where a 2D context
 *     exists; MapLibre has no per-layer blend API, so the value is retained
 *     in the store for engines that can honour it.
 *
 * Pure helpers here are unit-tested without a WebGL context. Adapters call
 * {@link applyTitilerRasterStyleToTemplate} and the paint mappers; the dock
 * fans a single apply call to whichever engine owns the container.
 */

import type { RasterBlendMode, RasterColorRamp, RasterToolCardState } from '@/state/useViewerStore';
import {
  RASTER_COLOR_RAMP_PRESETS,
  inferRasterRenderModeFromTemplate,
  normalizeRasterRenderMode,
  type RasterRenderMode,
} from '@/components/raster/rasterSymbology';
import {
  classBreaksToTitilerColormap,
  normalizeHexColor,
  rampToTitilerColormap,
  uniqueValuesToIntervalColormap,
  uniqueValuesToTitilerColormap,
  type RasterClassBreak,
  type RasterStretchType,
  type RasterUniqueValueRow,
} from '@/components/raster/rasterClassify';
import {
  HILLSHADE_BUFFER_PX,
  HILLSHADE_RESCALE,
  hillshadeAlgorithmParamsJson,
} from '@/utils/hillshade';
import {
  removeQueryParam,
  setBandIndices,
  upsertQueryParam,
} from '@/utils/titilerRasterProfile';
import { ensureTitilerPngTileTemplate } from '@/utils/titilerSourceHost';
import {
  GV_BACKGROUND_FILL_PARAM,
  GV_BACKGROUND_LEVEL_PARAM,
  GV_BACKGROUND_MASK_PARAM,
  GV_BACKGROUND_TOLERANCE_PARAM,
  resolveDisplayNodata,
  shouldApplyDisplayNodata,
} from '@/components/raster/rasterDra';

/** TiTiler `colormap_name` for each card ramp id. */
export const RASTER_COLOR_RAMP_TO_TITILER: Readonly<Record<RasterColorRamp, string>> =
  Object.fromEntries(
    RASTER_COLOR_RAMP_PRESETS.map((r) => [r.id, r.titilerName]),
  ) as Record<RasterColorRamp, string>;

const TITILER_TO_RASTER_COLOR_RAMP: Readonly<Record<string, RasterColorRamp>> = {
  'arcgis-elevation': 'arcgis-elevation',
  spectral: 'spectral',
  viridis: 'viridis',
  magma: 'magma',
  inferno: 'inferno',
  plasma: 'plasma',
  turbo: 'turbo',
  terrain: 'terrain',
  rdBu: 'RdBu',
  rdbu: 'RdBu',
  gray: 'greyscale',
  greyscale: 'greyscale',
  grey: 'greyscale',
};

/** Card fields the TiTiler rewrite may read. */
export type RasterStyleCardState = Pick<
  RasterToolCardState,
  'bands' | 'colorRamp' | 'noDataMask'
> & Partial<
  Pick<
    RasterToolCardState,
    | 'displayBackgroundValue'
    | 'backgroundValue'
    | 'backgroundTolerance'
    | 'backgroundColorMode'
    | 'backgroundColor'
  >
> & {
  readonly renderMode?: RasterRenderMode;
  readonly hillshadeZFactor?: number;
  readonly stretchType?: RasterStretchType;
  readonly stretchMin?: number | null;
  readonly stretchMax?: number | null;
  readonly classBreaks?: readonly RasterClassBreak[];
  readonly discreteColorCount?: number;
  readonly uniqueValues?: readonly RasterUniqueValueRow[];
  readonly symbologyDataMin?: number | null;
  readonly symbologyDataMax?: number | null;
};

export type TitilerRasterStyleOptions = {
  /**
   * When true the COG is multi-band imagery and a distinct RGB selection
   * should paint as true colour (no colormap / rescale). Single-band
   * selections still get the card's color ramp.
   */
  readonly multiBandImagery?: boolean;
  /** Sentinel used when `noDataMask` is on. `null` skips the param. */
  readonly nodata?: number | null;
  /**
   * Actual band count from `/cog/info`. When 1, RGB defaults from the card
   * must never be written into the tile URL — TiTiler returns
   * `band index 2 out of range` and every tile 500s (the DEM "disappears
   * when I click the layer" bug).
   */
  readonly bandCount?: number;
  /**
   * When true, leave the template's `nodata` query alone. Paired with
   * {@link inferRasterToolCardFromTileTemplate} on layer select.
   */
  readonly preserveNodata?: boolean;
  /**
   * When true, a single-band rewrite keeps the template's existing
   * `colormap_name` instead of forcing the card's default ramp. Used on
   * layer-select hydration so opening tools does not flash gray→viridis.
   */
  readonly preserveColormap?: boolean;
};

function readQueryValue(url: string, key: string): string | null {
  const match = new RegExp(`[?&]${key}=([^&]*)`).exec(url);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function readBandIndicesFromTemplate(url: string): number[] {
  const values: number[] = [];
  const re = /[?&]bidx=([^&]*)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(url)) !== null) {
    const n = Number(match[1]);
    if (Number.isFinite(n) && n >= 1) values.push(n);
  }
  return values;
}

/**
 * Mirror the live tile template into Raster ToolCard fields so selecting a
 * layer does not overwrite a DEM's gray stretch (or an RGB composite) with
 * the card's idle defaults (`bands: [1,2,3]`, `colorRamp: viridis`).
 */
export function inferRasterToolCardFromTileTemplate(
  tilesUrl: string,
  bandCount?: number,
): Partial<Pick<RasterToolCardState, 'bands' | 'colorRamp' | 'noDataMask' | 'renderMode'>> {
  const bidx = readBandIndicesFromTemplate(tilesUrl);
  let bands: [number, number, number] | undefined;
  if (typeof bandCount === 'number' && bandCount === 1) {
    bands = [1, 1, 1];
  } else if (bidx.length >= 3) {
    bands = [bidx[0], bidx[1], bidx[2]];
  } else if (bidx.length === 1) {
    bands = [bidx[0], bidx[0], bidx[0]];
  } else if (typeof bandCount === 'number' && bandCount >= 3) {
    bands = [1, 2, 3];
  }

  const colormap = readQueryValue(tilesUrl, 'colormap_name')?.toLowerCase();
  const colorRamp = colormap ? TITILER_TO_RASTER_COLOR_RAMP[colormap] : undefined;
  const renderMode = inferRasterRenderModeFromTemplate(tilesUrl, bandCount);

  const nodata = readQueryValue(tilesUrl, 'nodata');

  return {
    ...(bands ? { bands } : {}),
    ...(colorRamp ? { colorRamp } : {}),
    ...(renderMode ? { renderMode } : {}),
    // Mirror the live template: do not invent a nodata mask on select when the
    // mount URL never had one (that would suddenly punch holes in the DEM).
    noDataMask: nodata != null && nodata !== '',
  };
}

/**
 * True when the card is asking for a single band (R=G=B), which is how
 * operators force a color-ramp view on a multi-band file.
 */
export function isSingleBandSelection(bands: readonly [number, number, number]): boolean {
  return bands[0] === bands[1] && bands[1] === bands[2];
}

/**
 * Clamp card band indices to what the file actually has. A 1-band DEM with
 * the card's default `[1, 2, 3]` becomes `[1]`; indices above `bandCount`
 * collapse to `bandCount`.
 */
export function effectiveBandIndices(
  bands: readonly [number, number, number],
  bandCount?: number,
): number[] {
  if (typeof bandCount === 'number' && bandCount > 0 && bandCount < 3) {
    const only = Math.min(Math.max(1, bands[0]), bandCount);
    return [only];
  }
  if (isSingleBandSelection(bands)) {
    return [Math.max(1, bands[0])];
  }
  return [
    Math.max(1, bands[0]),
    Math.max(1, bands[1]),
    Math.max(1, bands[2]),
  ].map((band) =>
    typeof bandCount === 'number' && bandCount > 0 ? Math.min(band, bandCount) : band,
  );
}

/**
 * Where the mount-time contrast stretch waits out hillshade mode, which needs
 * `rescale` for its own illumination range. TiTiler ignores query params it
 * does not declare, so this rides along harmlessly and keeps the tile URL the
 * single source of truth — the same way render mode is read back from it.
 */
const PARKED_RESCALE_PARAM = 'gv_parked_rescale';

function applyNodata(
  tilesUrl: string,
  state: Pick<RasterToolCardState, 'noDataMask'> & Partial<
    Pick<
      RasterToolCardState,
      | 'displayBackgroundValue'
      | 'backgroundValue'
      | 'backgroundTolerance'
      | 'backgroundColorMode'
      | 'backgroundColor'
    >
  >,
  options: TitilerRasterStyleOptions,
): string {
  if (options.preserveNodata) {
    // Keep mount-time masking (and any markers that came with it).
    return tilesUrl;
  }
  const normalized = {
    noDataMask: state.noDataMask,
    displayBackgroundValue: state.displayBackgroundValue ?? false,
    backgroundValue: state.backgroundValue ?? 0,
  };

  let out = applyBackgroundKeyMarkers(tilesUrl, state);

  const effective = resolveDisplayNodata(normalized, options.nodata ?? null);
  if (effective != null) {
    return upsertQueryParam(out, 'nodata', String(effective));
  }
  if (!shouldApplyDisplayNodata(normalized)) {
    return removeQueryParam(out, 'nodata');
  }
  // Mask requested but no numeric sentinel yet — leave `nodata` alone.
  return out;
}

/**
 * Stamp (or clear) the client-side background-key markers.
 *
 * Server-side `nodata` is exact-match only, so the resampled collar around a
 * scene survives it — and everything it does mask comes back transparent, with
 * no way to ask for a colour. These markers tell the engine tile
 * post-processor which display level to key out, how much slack to allow, and
 * what to paint. With no tolerance and no colour there is nothing left for the
 * client to do, so the markers stay off and tiles skip post-processing.
 */
function applyBackgroundKeyMarkers(
  tilesUrl: string,
  state: Partial<
    Pick<
      RasterToolCardState,
      | 'displayBackgroundValue'
      | 'backgroundValue'
      | 'backgroundTolerance'
      | 'backgroundColorMode'
      | 'backgroundColor'
    >
  >,
): string {
  let out = removeQueryParam(tilesUrl, GV_BACKGROUND_MASK_PARAM);
  out = removeQueryParam(out, GV_BACKGROUND_LEVEL_PARAM);
  out = removeQueryParam(out, GV_BACKGROUND_TOLERANCE_PARAM);
  out = removeQueryParam(out, GV_BACKGROUND_FILL_PARAM);

  if (!state.displayBackgroundValue) return out;

  const tolerance = Math.min(255, state.backgroundTolerance ?? 0);
  const fill =
    state.backgroundColorMode === 'color'
      ? normalizeHexColor(state.backgroundColor ?? '')
      : null;
  if (!(tolerance > 0) && !fill) return out;

  const level = Math.max(0, Math.min(255, state.backgroundValue ?? 0));
  out = upsertQueryParam(out, GV_BACKGROUND_MASK_PARAM, '1');
  out = upsertQueryParam(out, GV_BACKGROUND_LEVEL_PARAM, String(level));
  out = upsertQueryParam(
    out,
    GV_BACKGROUND_TOLERANCE_PARAM,
    String(Math.max(0, tolerance)),
  );
  if (fill) {
    out = upsertQueryParam(out, GV_BACKGROUND_FILL_PARAM, fill.slice(1));
  }
  return out;
}

/**
 * Rewrite a TiTiler XYZ template so it matches the card's bands / ramp /
 * render mode / no-data mask. Idempotent: repeated applies with the same
 * state yield the same URL.
 *
 * Never invents bands the file does not have. Until `bandCount` is known,
 * callers must not invoke this for source rewrites (paint-only is fine).
 */
export function applyTitilerRasterStyleToTemplate(
  tilesUrl: string,
  state: RasterStyleCardState,
  options: TitilerRasterStyleOptions = {},
): string {
  let out = ensureTitilerPngTileTemplate(tilesUrl);
  const rawMode = state.renderMode;
  const mode = normalizeRasterRenderMode(rawMode);
  const bandCount = options.bandCount;

  // Strip previous symbology algorithm / custom colormap / mode markers
  // unless this apply re-applies them.
  out = removeQueryParam(out, 'algorithm');
  out = removeQueryParam(out, 'algorithm_params');
  out = removeQueryParam(out, 'buffer');
  out = removeQueryParam(out, 'colormap');
  out = removeQueryParam(out, 'gv_render');
  out = removeQueryParam(out, 'gv_shaded_relief');
  out = removeQueryParam(out, 'gv_sr_cmap');
  out = removeQueryParam(out, 'gv_sr_rescale');
  if (readQueryValue(out, 'rescale') === HILLSHADE_RESCALE) {
    out = removeQueryParam(out, 'rescale');
  }

  if (mode === 'hillshade' || mode === 'shaded-relief') {
    // Park the mount-time stretch before overwriting `rescale`. Without this,
    // DEM → hillshade → Stretch came back with NO stretch at all.
    const mountRescale = readQueryValue(out, 'rescale');
    if (mountRescale) {
      out = upsertQueryParam(out, PARKED_RESCALE_PARAM, mountRescale);
    }

    if (mode === 'shaded-relief') {
      // ArcGIS Shaded Relief = elevation color ramp × hillshade illumination.
      // Compact twin markers (ramp + rescale) ride on the hillshade URL so
      // engines can rebuild the colour twin without nesting a second full URL
      // (which blows past practical query-string limits).
      const stretch =
        resolveStretchRescale(state, out)
        ?? readQueryValue(out, 'rescale')
        ?? readQueryValue(out, PARKED_RESCALE_PARAM);
      out = setBandIndices(out, [1]);
      out = removeQueryParam(out, 'colormap_name');
      out = removeQueryParam(out, 'colormap');
      out = upsertQueryParam(out, 'algorithm', 'hillshade');
      out = upsertQueryParam(
        out,
        'algorithm_params',
        hillshadeAlgorithmParamsJson({ zFactor: state.hillshadeZFactor }),
      );
      out = upsertQueryParam(out, 'buffer', String(HILLSHADE_BUFFER_PX));
      out = upsertQueryParam(out, 'rescale', HILLSHADE_RESCALE);
      out = upsertQueryParam(out, 'gv_shaded_relief', '1');
      out = upsertQueryParam(
        out,
        'gv_sr_cmap',
        state.colorRamp,
      );
      if (stretch && stretch !== HILLSHADE_RESCALE) {
        out = upsertQueryParam(out, 'gv_sr_rescale', stretch);
      }
      return applyNodata(out, state, options);
    }

    out = setBandIndices(out, [1]);
    out = removeQueryParam(out, 'colormap_name');
    out = upsertQueryParam(out, 'algorithm', 'hillshade');
    out = upsertQueryParam(
      out,
      'algorithm_params',
      hillshadeAlgorithmParamsJson({ zFactor: state.hillshadeZFactor }),
    );
    out = upsertQueryParam(out, 'buffer', String(HILLSHADE_BUFFER_PX));
    out = upsertQueryParam(out, 'rescale', HILLSHADE_RESCALE);
    return applyNodata(out, state, options);
  }

  // Leaving hillshade / shaded relief: restore parked stretch, drop colour twin.
  out = removeQueryParam(out, 'gv_sr_cmap');
  out = removeQueryParam(out, 'gv_sr_rescale');
  const parkedRescale = readQueryValue(out, PARKED_RESCALE_PARAM);
  out = removeQueryParam(out, PARKED_RESCALE_PARAM);
  if (parkedRescale) {
    out = upsertQueryParam(out, 'rescale', parkedRescale);
  }

  if (mode === 'multiband-color') {
    const indices = effectiveBandIndices(
      state.bands[0] === state.bands[1] && state.bands[1] === state.bands[2]
        ? [1, 2, 3]
        : state.bands,
      bandCount,
    );
    if (indices.length >= 3 && (typeof bandCount !== 'number' || bandCount >= 3)) {
      out = setBandIndices(out, indices.slice(0, 3));
      out = removeQueryParam(out, 'colormap_name');
      out = removeQueryParam(out, 'rescale');
    } else {
      out = setBandIndices(out, [indices[0] ?? 1]);
      out = upsertQueryParam(out, 'colormap_name', 'gray');
    }
  } else if (mode === 'stretch' || mode === 'singleband-gray' || mode === 'singleband-pseudocolor') {
    const indices = effectiveBandIndices(
      [state.bands[0], state.bands[0], state.bands[0]],
      bandCount,
    );
    out = setBandIndices(out, indices);
    const existingColormap = readQueryValue(out, 'colormap_name');
    const wantGray =
      state.colorRamp === 'greyscale'
      || rawMode === 'singleband-gray';
    const existingIsGray =
      existingColormap != null
      && ['gray', 'greyscale', 'grey'].includes(existingColormap.toLowerCase());
    if (options.preserveColormap && existingIsGray && wantGray) {
      // Leave mount-time gray stretch alone.
    } else {
      out = upsertQueryParam(
        out,
        'colormap_name',
        wantGray ? 'gray' : (RASTER_COLOR_RAMP_TO_TITILER[state.colorRamp] ?? 'viridis'),
      );
    }
    const stretch = resolveStretchRescale(state, out);
    if (stretch) {
      out = upsertQueryParam(out, 'rescale', stretch);
    } else if (state.stretchType === 'none') {
      out = removeQueryParam(out, 'rescale');
    }
  } else if (mode === 'classify' || mode === 'discrete' || mode === 'contours') {
    const indices = effectiveBandIndices(
      [state.bands[0], state.bands[0], state.bands[0]],
      bandCount,
    );
    out = setBandIndices(out, indices);
    out = removeQueryParam(out, 'colormap_name');
    out = removeQueryParam(out, 'rescale');
    const dataMin = state.symbologyDataMin;
    const breaks = state.classBreaks ?? [];
    if (breaks.length > 0 && typeof dataMin === 'number' && Number.isFinite(dataMin)) {
      out = upsertQueryParam(out, 'colormap', classBreaksToTitilerColormap(breaks, dataMin));
    } else {
      // Until stats populate class breaks, fall back to a continuous ramp so
      // the map never goes blank mid-switch.
      out = upsertQueryParam(
        out,
        'colormap_name',
        RASTER_COLOR_RAMP_TO_TITILER[state.colorRamp] ?? 'terrain',
      );
    }
    out = upsertQueryParam(out, 'gv_render', mode === 'discrete' || mode === 'contours' ? 'discrete' : 'classify');
  } else if (mode === 'unique-values') {
    const indices = effectiveBandIndices(
      [state.bands[0], state.bands[0], state.bands[0]],
      bandCount,
    );
    out = setBandIndices(out, indices);
    out = removeQueryParam(out, 'colormap_name');
    out = removeQueryParam(out, 'rescale');
    const rows = state.uniqueValues ?? [];
    const dataMin = state.symbologyDataMin;
    const dataMax = state.symbologyDataMax;
    if (rows.length > 0) {
      const allInt = rows.every((r) => Number.isInteger(r.value));
      if (
        allInt
        && typeof dataMin === 'number'
        && typeof dataMax === 'number'
        && dataMax - dataMin <= 64
      ) {
        out = upsertQueryParam(out, 'colormap', uniqueValuesToTitilerColormap(rows));
      } else if (typeof dataMin === 'number' && typeof dataMax === 'number') {
        out = upsertQueryParam(
          out,
          'colormap',
          uniqueValuesToIntervalColormap(rows, dataMin, dataMax),
        );
      } else {
        out = upsertQueryParam(out, 'colormap_name', 'accent');
      }
    } else {
      out = upsertQueryParam(out, 'colormap_name', 'accent');
    }
    out = upsertQueryParam(out, 'gv_render', 'unique');
  } else if (mode === 'vector-field') {
    // Placeholder — keep current single-band look until vector glyphs ship.
    const indices = effectiveBandIndices(
      [state.bands[0], state.bands[0], state.bands[0]],
      bandCount,
    );
    out = setBandIndices(out, indices);
    out = upsertQueryParam(
      out,
      'colormap_name',
      RASTER_COLOR_RAMP_TO_TITILER[state.colorRamp] ?? 'viridis',
    );
  } else {
    // Legacy path (no renderMode): previous band/ramp heuristics.
    const indices = effectiveBandIndices(state.bands, bandCount);
    const single = indices.length === 1;
    const wantRgb =
      Boolean(options.multiBandImagery)
      && !single
      && indices.length >= 3
      && (typeof bandCount !== 'number' || bandCount >= 3);

    if (wantRgb) {
      out = setBandIndices(out, indices.slice(0, 3));
      out = removeQueryParam(out, 'colormap_name');
      out = removeQueryParam(out, 'rescale');
    } else if (single) {
      out = setBandIndices(out, indices);
      const existingColormap = readQueryValue(out, 'colormap_name');
      if (options.preserveColormap && existingColormap) {
        // Leave the mount-time stretch alone (DEM gray, etc.).
      } else {
        out = upsertQueryParam(
          out,
          'colormap_name',
          RASTER_COLOR_RAMP_TO_TITILER[state.colorRamp] ?? 'gray',
        );
      }
    } else if (typeof bandCount === 'number' && bandCount >= 3) {
      out = setBandIndices(out, indices.slice(0, 3));
      out = removeQueryParam(out, 'colormap_name');
    } else {
      out = setBandIndices(out, [indices[0] ?? 1]);
    }
  }

  return applyNodata(out, state, options);
}

/** Resolve TiTiler `rescale` from card stretch controls + parked/mount URL. */
function resolveStretchRescale(
  state: RasterStyleCardState,
  tilesUrl: string,
): string | null {
  if (
    typeof state.stretchMin === 'number'
    && typeof state.stretchMax === 'number'
    && Number.isFinite(state.stretchMin)
    && Number.isFinite(state.stretchMax)
    && state.stretchMax > state.stretchMin
  ) {
    return `${state.stretchMin},${state.stretchMax}`;
  }
  if (state.stretchType === 'none') return null;
  const existing = readQueryValue(tilesUrl, 'rescale');
  if (existing && existing !== HILLSHADE_RESCALE) return existing;
  if (
    typeof state.symbologyDataMin === 'number'
    && typeof state.symbologyDataMax === 'number'
    && state.symbologyDataMax > state.symbologyDataMin
  ) {
    return `${state.symbologyDataMin},${state.symbologyDataMax}`;
  }
  return null;
}

/**
 * Rebuild the elevation-colour twin for a shaded-relief hillshade tile URL.
 * Returns null when the URL is not marked as shaded relief.
 */
export function buildShadedReliefColorTileUrl(hillshadeTileUrl: string): string | null {
  if (!isShadedReliefTileUrl(hillshadeTileUrl)) return null;
  const cmap = readQueryValue(hillshadeTileUrl, 'gv_sr_cmap') ?? 'terrain';
  const stretch = readQueryValue(hillshadeTileUrl, 'gv_sr_rescale');
  let color = hillshadeTileUrl;
  color = removeQueryParam(color, 'algorithm');
  color = removeQueryParam(color, 'algorithm_params');
  color = removeQueryParam(color, 'buffer');
  color = removeQueryParam(color, 'colormap');
  color = removeQueryParam(color, 'gv_shaded_relief');
  color = removeQueryParam(color, 'gv_sr_cmap');
  color = removeQueryParam(color, 'gv_sr_rescale');
  color = removeQueryParam(color, PARKED_RESCALE_PARAM);
  color = removeQueryParam(color, 'gv_render');
  if (cmap === 'arcgis-elevation' || cmap === 'spectral') {
    color = removeQueryParam(color, 'colormap_name');
    color = upsertQueryParam(
      color,
      'colormap',
      rampToTitilerColormap(cmap),
    );
  } else {
    color = upsertQueryParam(
      color,
      'colormap_name',
      RASTER_COLOR_RAMP_TO_TITILER[cmap as RasterColorRamp] ?? cmap,
    );
  }
  if (stretch) {
    color = upsertQueryParam(color, 'rescale', stretch);
  } else {
    color = removeQueryParam(color, 'rescale');
  }
  return color;
}

export function isShadedReliefTileUrl(tilesUrl: string): boolean {
  return readQueryValue(tilesUrl, 'gv_shaded_relief') === '1';
}

export type MapLibreRasterPaint = {
  readonly opacity: number;
  readonly brightnessMax: number;
  readonly contrast: number;
  readonly saturation: number;
};

/**
 * MapLibre paint values for the card's display sliders.
 * Contrast / saturation on the card are multipliers around 1; MapLibre wants
 * an offset in [-1, 1] (0 = unchanged).
 */
export function mapLibreRasterPaintFromState(
  state: Pick<RasterToolCardState, 'opacity' | 'brightness' | 'contrast' | 'saturation'>,
): MapLibreRasterPaint {
  return {
    opacity: clamp(state.opacity, 0, 1),
    brightnessMax: clamp(state.brightness, 0, 1),
    contrast: clamp(state.contrast - 1, -1, 1),
    saturation: clamp(state.saturation - 1, -1, 1),
  };
}

/**
 * CSS `filter` for OpenLayers prerender (Canvas 2D). Brightness / contrast /
 * saturation are the card multipliers; opacity is applied via `layer.setOpacity`
 * instead so hit-testing and fade stay correct.
 */
export function openLayersCanvasFilterFromState(
  state: Pick<RasterToolCardState, 'brightness' | 'contrast' | 'saturation'>,
): string {
  const brightness = clamp(state.brightness, 0.5, 1.5);
  const contrast = clamp(state.contrast, 0.5, 1.5);
  const saturation = clamp(state.saturation, 0, 2);
  return `brightness(${brightness}) contrast(${contrast}) saturate(${saturation})`;
}

/** Canvas 2D composite op for the card's blend mode, or null for "normal". */
export function openLayersCompositeFromBlend(blending: RasterBlendMode): GlobalCompositeOperation | null {
  switch (blending) {
    case 'multiply':
      return 'multiply';
    case 'screen':
      return 'screen';
    case 'overlay':
      return 'overlay';
    case 'normal':
    default:
      return null;
  }
}

function clamp(value: number, lo: number, hi: number): number {
  if (!Number.isFinite(value)) return lo;
  return Math.max(lo, Math.min(hi, value));
}
